import { batch } from "solid-js";
import { createStore, produce, reconcile } from "solid-js/store";
import type { DbEntities } from "./getLocalDb";

export const createEntitySyncManager = <Entity extends DbEntities[keyof DbEntities]>(
  getFuzzyText?: (entity: Entity) => string[]
) => {
  type Id = Entity["id"];
  type EntityStore = Record<Id, Entity>;

  const [data, setData] = createStore<{ ids: Id[]; byId: EntityStore }>({
    ids: [],
    byId: {} as EntityStore,
  });

  const searchIds = (query: string): Id[] => {
    if (!getFuzzyText) return data.ids;
    if (!query) return data.ids;

    return all()
      .filter((d) =>
        getFuzzyText(d).some((t) =>
          t.replaceAll(" ", "").toLowerCase().includes(query.replaceAll(" ", "").toLowerCase())
        )
      )
      .map((d) => d.id);
  };
  const search = (query: string): Entity[] => {
    if (!query) return all();
    return searchIds(query)
      .map((id) => data.byId[id])
      .filter(Boolean);
  };

  // A parked transition has already written the raw store while reads still return committed
  // values: enumerating byId turns entities it added into undefined, and walking ids (whose
  // length is committed) turns entities it removed into holes. The filter drops both.
  const all = (): Entity[] => data.ids.map((id) => data.byId[id]).filter(Boolean);

  const set = (id: Id, record: Entity) => {
    batch(() => {
      setData("ids", reconcile(Array.from(new Set([...data.ids, id]))));
      // oxlint-disable-next-line typescript/no-explicit-any -- The generics are too complex for ts to infer
      setData("byId", id as any, reconcile(record as any));
    });
  };

  const remove = (id: Id) => {
    setData(
      produce((store) => {
        store.ids = store.ids.filter((i) => i !== id);
        delete store.byId[id];
      })
    );
  };

  const get = (id: Id): Entity | undefined => data.byId[id];

  const hydrateFromSync = (records: Entity[]) => {
    const ids: Id[] = [];
    const byId = {} as EntityStore;
    for (const record of records) {
      ids.push(record.id);
      byId[record.id as Id] = record;
    }
    setData(reconcile({ ids, byId }, { merge: true }));
  };

  return {
    set,
    remove,
    get,
    hydrateFromSync,
    searchIds,
    search,
    all,
  };
};
